import moment from 'moment';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, HelpTooltip, Icon, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import settings from '~/settings.js';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import CancelSubscription from './CancelSubscription';
import EditContact from './EditContact';
import EditPaymentMethod from './EditPaymentMethod';
import EditPlan from './EditPlan';
import RemovePaymentMethod from './RemovePaymentMethod';
import UndoCancelSubscription from './UndoCancelSubscription';
import ViewBillingHistory from './ViewBillingHistory';
import ViewInvoices from './ViewInvoices';

const Blocks = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  max-width: 64rem;
  margin: -1rem -0.75rem;
  margin-top: 1rem;
`;

const Block = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: calc(50% - 1.5rem);
  margin: 1rem 0.75rem;
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  border-radius: 0.3125rem;
`;

const HighlightBlock = styled(Block)`
  background-color: ${colors.primary5};
`;

const WarningHighlightBlock = styled(Block)`
  background-color: ${colors.warning10};
`;

const DangerHighlightBlock = styled(Block)`
  background-color: ${colors.danger5};
`;

const WarningIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  color: ${({ isError }) => (isError ? colors.error : colors.warning)};
  font-size: 2rem;
`;

const Content = styled.div`
  padding: 1rem 1.5rem;
`;

const Title = styled.h3`
  font-size: 1.125rem;
  line-height: 1.5rem;
`;

const Label = styled.div`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
`;

const Value = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
`;

const ValueLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

const TooltipList = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1rem;
  list-style-type: disc;
  list-style-position: outside;
`;

const NoValue = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
  color: ${colors.grey55};
`;

const Description = styled.div`
  margin-top: 0.75rem;
`;

const Message = styled.div`
  margin: 1rem 0.75rem;
  padding: 1rem 1.5rem;
  background-color: ${colors.grey5};
  border-radius: 0.25rem;
`;

const WarningMessage = styled(Message)`
  background-color: ${colors.warning};
`;

const ErrorMessage = styled(Message)`
  color: ${colors.white};
  background-color: ${colors.error};
`;

const Actions = styled.div`
  margin-top: auto;
  padding: 0.75rem 1.5rem;
  border-top: solid 1px ${colors.grey10};

  > *:not(:first-child) {
    margin-left: 1rem;
  }
`;

const Invoices = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr 1.5fr 1.5fr 1fr;
  row-gap: 0.25rem;
  width: 100%;

  ${Label} {
    margin-bottom: 0.25rem;
  }
`;

const InvoiceData = styled.div`
  display: flex;
  justify-content: ${({ align }) => (align === 'right' ? 'flex-end' : 'flex-start')};
  color: ${({ noValue }) => (noValue ? colors.grey55 : colors.black)};
`;

const ExternalAnchor = styled.a`
  display: inline-flex;
  align-items: center;
`;

const ExternalIcon = styled(Icon)`
  margin-left: 0.25rem;
  font-size: 0.75rem;
`;

const currency = new Intl.NumberFormat('en-us', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default function BillingInfo({ billingData, setBillingData }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const api = useApi();
  const toast = useToast();
  const { workspace, workspaceId } = useWorkspace();

  const overview = billingData?.overview || {};
  const plan = billingData?.plan || {};
  const subscription = billingData?.subscription || {};
  const payment = billingData?.payment || {};
  const contact = billingData?.contact || {};
  const balances = billingData?.balances || {};
  const invoicesPreview = billingData?.invoicesPreview || { count: 0, items: [] };
  const actions = billingData?.actions || {};

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [trialExtensionLoading, setTrialExtensionLoading] = useState(false);

  const [cancelSubscription, setCancelSubscription] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [editPaymentMethod, setEditPaymentMethod] = useState(false);
  const [editPlan, setEditPlan] = useState(params.has('changePlan'));
  const [removePaymentMethod, setRemovePaymentMethod] = useState(false);
  const [undoCancelSubscription, setUndoCancelSubscription] = useState(false);
  const [viewBillingHistory, setViewBillingHistory] = useState(false);
  const [viewInvoices, setViewInvoices] = useState(false);

  const requestTrialExtension = async () => {
    setTrialExtensionLoading(true);
    try {
      await api.www.workspaces(workspace.id).trialRequest();
      toast.success('Your request has been sent to our team. We will be in touch shortly regarding a new free trial.');
    } catch ({ message }) {
      toast.error(message);
    }
    setTrialExtensionLoading(false);
  };

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    const { data } = await api.www.workspaces(workspaceId).billing.create();
    setIsSubscribing(false);
    setBillingData(data || null);
  };

  const planInfo = {
    name: plan.productName || 'Team Plan',
    members: overview.chargeableMembers ?? workspace.billingStatus?.memberCount ?? 0,
    frequency: plan.billingFrequencyLabel || null,
    cost: plan.monthlySeatPrice || 0,
  };

  let trialInfo = null;
  const trialEndsAt = overview?.trialEndsAt;
  if (trialEndsAt && moment(trialEndsAt).isAfter(moment())) {
    const trialEnd = moment(trialEndsAt);
    trialInfo = {
      daysLeft: Math.max(Math.ceil(trialEnd.diff(moment(), 'days', true)), 0),
      expirationDate: trialEnd.format(dateFormats.longDate),
    };
  }

  let subscriptionMessage = null;
  if (trialInfo && actions.canSubscribe) {
    // No message — the "Get Started" block handles this case
  } else if (trialInfo && overview.isConfigured) {
    subscriptionMessage = (
      <Message>
        Your trial is active with{' '}
        <strong>
          {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'}
        </strong>{' '}
        remaining and your billing is all set. Your subscription will begin on {trialInfo.expirationDate}. You will not
        be charged until your trial ends.
      </Message>
    );
  } else if (trialInfo && !overview.isConfigured && trialInfo.daysLeft <= 5) {
    subscriptionMessage = (
      <WarningMessage>
        Your trial ends in{' '}
        <strong>
          {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'}
        </strong>
        . To avoid an interruption of service, ensure that your contact information, credit card, and plan selection are
        provided below. You will not be charged until your trial ends.
      </WarningMessage>
    );
  } else if (trialInfo) {
    subscriptionMessage = (
      <Message>
        Your trial is active with{' '}
        <strong>
          {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'}
        </strong>{' '}
        remaining. To ensure uninterrupted service, provide your contact information and credit card below. You will not
        be charged until your trial ends.
      </Message>
    );
  } else if (!trialInfo && !subscription.exists) {
    subscriptionMessage = (
      <ErrorMessage>
        A subscription is required to use Ruddr. Please ensure that your contact information, credit card, and billing
        frequency are provided below.
      </ErrorMessage>
    );
  } else if (subscription.exists && !subscription.isActive) {
    subscriptionMessage = (
      <ErrorMessage>
        Your subscription has an issue. Please ensure that your contact information, credit card, and plan selection are
        provided below.
      </ErrorMessage>
    );
  } else if (subscription.isPastDue) {
    subscriptionMessage = (
      <ErrorMessage>
        {subscription.isManualPayment
          ? 'Your subscription is past due. Please ensure that any outstanding invoices are paid to prevent getting locked out of your workspace.'
          : 'Your subscription is past due. Please ensure that a valid credit card has been added to prevent getting locked out of your workspace.'}
      </ErrorMessage>
    );
  }

  const paymentEnabled = !!settings.stripePubKey && payment.canEdit;
  let paymentMessage = null;
  if (!settings.stripePubKey) {
    paymentMessage = 'Credit card processing is currently disabled.';
  } else if (payment.message) {
    paymentMessage = payment.message;
  }
  const associatedWorkspaces = Array.isArray(overview.associatedWorkspaces) ? overview.associatedWorkspaces : [];

  const formatCurrency = (amount) => (amount % 1 != 0 ? amount.toFixed(2) : amount);

  return (
    <>
      <Blocks>
        {subscriptionMessage}
        <Block>
          <Content>
            <Title>Plan Details</Title>
            <Label>Name</Label>
            <Value>{planInfo.name}</Value>
            {!!planInfo.frequency && (
              <>
                <Label>Plan Renewal Frequency</Label>
                <Value>{planInfo.frequency}</Value>
              </>
            )}
            {!!planInfo.cost && (
              <>
                <Label>Monthly Cost</Label>
                <Value>${formatCurrency(planInfo.cost)} per member</Value>
              </>
            )}
            <Label>
              Chargeable Members
              {associatedWorkspaces.length > 0 && (
                <HelpTooltip
                  style={{ marginLeft: '0.25rem' }}
                  placement="top"
                  message={
                    <>
                      <div>This workspace also handles billing for the following workspaces:</div>
                      <TooltipList>
                        {associatedWorkspaces.map((item) => (
                          <li key={item.id}>
                            {item.name && item.key ? `${item.name} (${item.key})` : item.name || item.key || item.id}
                          </li>
                        ))}
                      </TooltipList>
                    </>
                  }
                />
              )}
            </Label>
            <ValueLine>
              <div>{planInfo.members}</div>
              {actions.hasBillingEvents && (
                <Button isAnchor={true} onClick={() => setViewBillingHistory(true)}>
                  View History
                </Button>
              )}
            </ValueLine>
          </Content>
          <Actions>
            <Button isAnchor={true} onClick={() => setEditPlan(true)}>
              Change Plan
            </Button>
          </Actions>
        </Block>
        <Block>
          {!trialInfo && subscription.exists && (!subscription.isActive || subscription.isPastDue) && (
            <WarningIndicator isError={true}>
              <Tooltip message="There is a problem with your subscription. Ensure a valid credit card has been added.">
                <Icon icon="exclamation-triangle" />
              </Tooltip>
            </WarningIndicator>
          )}
          <Content>
            <Title>Subscription Status</Title>
            <Label>Status</Label>
            {trialInfo ? (
              <Value>In trial</Value>
            ) : subscription.exists ? (
              <Value>
                {subscription.statusLabel}
                {subscription.cancelAtPeriodEnd && <> (Pending Cancellation)</>}
              </Value>
            ) : (
              <NoValue>No subscription</NoValue>
            )}
            {!!subscription.cancelAtPeriodEnd && (
              <>
                <Label>Cancellation Date</Label>
                <Value>{moment.unix(subscription.cancelAt).format(dateFormats.longDate)}</Value>
              </>
            )}
            {trialInfo ? (
              <>
                <Label>Trial Days Left</Label>
                <Value>{trialInfo.daysLeft}</Value>
                <Label>Trial Expiration Date</Label>
                <Value>{trialInfo.expirationDate}</Value>
              </>
            ) : subscription.exists && !subscription.cancelAtPeriodEnd ? (
              <>
                <Label>Next Billing Period</Label>
                <Value>{moment.unix(subscription.currentPeriodEnd).format(dateFormats.longDate)}</Value>
              </>
            ) : null}
            {!!billingData && (
              <>
                <Label>Workspace Balance</Label>
                <Value>
                  {currency.format((balances.workspaceBalanceCents || 0) / 100) + ' USD '}
                  {(balances.workspaceBalanceCents || 0) < 0 && (
                    <>
                      (credit)
                      <HelpTooltip
                        style={{ marginLeft: '0.25rem' }}
                        message="Your credit balance will automatically be applied to your next invoice. Credit balances are not applied to past open invoices."
                        placement="right"
                      />
                    </>
                  )}
                </Value>
                {Number.isInteger(balances.unappliedCashBalanceCents) && (
                  <>
                    <Label>Unapplied Cash Balance</Label>
                    <Value>
                      {currency.format(balances.unappliedCashBalanceCents / 100) + ' USD '}
                      {balances.unappliedCashBalanceCents > 0 && (
                        <HelpTooltip
                          style={{ marginLeft: '0.25rem' }}
                          message="These funds have been received by Ruddr but do not match an existing invoice. Please make an additional payment to clear any outstanding invoices."
                          placement="right"
                        />
                      )}
                    </Value>
                  </>
                )}
              </>
            )}
          </Content>
          {!!subscription.isActive && (
            <Actions>
              {subscription.cancelAtPeriodEnd ? (
                <Button isAnchor={true} onClick={() => setUndoCancelSubscription(true)}>
                  Undo Cancellation
                </Button>
              ) : (
                <Button isAnchor={true} onClick={() => setCancelSubscription(true)}>
                  Cancel Subscription
                </Button>
              )}
            </Actions>
          )}
        </Block>
        {!actions.canSubscribe && (
          <Block>
            {!contact.addressLine1 && (
              <WarningIndicator>
                <Tooltip message="Please add a valid address.">
                  <Icon icon="exclamation-triangle" />
                </Tooltip>
              </WarningIndicator>
            )}
            <Content>
              <Title>Billing Contact</Title>
              <Label>Email</Label>
              {contact.email ? <Value>{contact.email}</Value> : <NoValue>No email</NoValue>}
              <Label>Company Name</Label>
              {contact.name ? <Value>{contact.name}</Value> : <NoValue>No name</NoValue>}
              <Label>Billing Address</Label>
              {contact.addressLine1 ? (
                <Value>
                  {contact.addressLine1}
                  <br />
                  {contact.addressLine2 && (
                    <>
                      {contact.addressLine2}
                      <br />
                    </>
                  )}
                  {contact.addressCity} {contact.addressState}, {contact.addressPostalCode}
                  <br />
                  {contact.countryName || contact.addressCountry}
                </Value>
              ) : (
                <NoValue>No address</NoValue>
              )}
            </Content>
            <Actions>
              <Button isAnchor={true} onClick={() => setEditContact(true)}>
                Update Contact Info
              </Button>
            </Actions>
          </Block>
        )}
        {!actions.canSubscribe && (
          <Block>
            {!payment.hasCard && payment.mode !== 'manual' && (
              <WarningIndicator>
                <Tooltip message="Please add a credit card.">
                  <Icon icon="exclamation-triangle" />
                </Tooltip>
              </WarningIndicator>
            )}
            <Content>
              <Title>Payment Information</Title>
              {payment.mode === 'manual' ? (
                <Description>
                  This workspace is set up for manual payments. An invoice will be emailed to the billing contact at the
                  end of each billing period.
                </Description>
              ) : (
                <>
                  <Label>Credit Card</Label>
                  {payment.hasCard ? (
                    <>
                      <Value>
                        {payment.cardBrand} ending in {payment.cardLast4}
                      </Value>
                      <Label>Expires</Label>
                      <Value>
                        {String(payment.cardExpMonth).padStart(2, '0')} / {payment.cardExpYear}
                      </Value>
                    </>
                  ) : (
                    <NoValue>No card</NoValue>
                  )}
                </>
              )}
            </Content>
            {payment.mode !== 'manual' && (
              <Actions>
                <Button isAnchor={true} disabled={!paymentEnabled} onClick={() => setEditPaymentMethod(true)}>
                  <Tooltip message={paymentMessage}>{payment.hasCard ? 'Change' : 'Add'} Credit Card</Tooltip>
                </Button>
                {!!payment.hasCard && (
                  <Button isAnchor={true} onClick={() => setRemovePaymentMethod(true)}>
                    Remove Credit Card
                  </Button>
                )}
              </Actions>
            )}
          </Block>
        )}
        {invoicesPreview.count > 0 && (
          <Block>
            <Content>
              <Title>Invoices</Title>
              <Invoices>
                <Label>Number</Label>
                <Label>Date</Label>
                <Label>Status</Label>
                <Label align="right">Total</Label>
                <Label align="right">PDF</Label>
                {invoicesPreview.items.map((invoice) => (
                  <React.Fragment key={invoice.id}>
                    <InvoiceData noValue={!invoice.number}>{invoice.number ?? 'N/A'}</InvoiceData>
                    <InvoiceData>{moment.unix(invoice.created).format(dateFormats.compactDate)}</InvoiceData>
                    <InvoiceData>{invoice.stripeStatus.name}</InvoiceData>
                    <InvoiceData align="right">${(invoice.total / 100).toFixed(2)}</InvoiceData>
                    <InvoiceData align="right">
                      {invoice.invoicePdf && (
                        <a href={invoice.invoicePdf}>
                          <Icon icon="file-pdf" />
                        </a>
                      )}
                    </InvoiceData>
                  </React.Fragment>
                ))}
              </Invoices>
            </Content>
            {invoicesPreview.count > 5 && (
              <Actions>
                <Button isAnchor={true} onClick={() => setViewInvoices(true)}>
                  View All Invoices
                </Button>
              </Actions>
            )}
          </Block>
        )}
        <Block>
          <Content>
            <Title>Contact Us</Title>
            <Description>
              For billing questions, check out the help article below or contact our support team via email or by
              submitting a support request.
            </Description>
          </Content>
          <Actions>
            <ExternalAnchor
              href="https://help.ruddr.io/hc/en-us/articles/1500003367762-Billing"
              target="_blank"
              rel="noreferrer">
              Help Article <ExternalIcon icon="external-link-alt" />
            </ExternalAnchor>
            <ExternalAnchor href="https://help.ruddr.io/hc/en-us/requests/new" target="_blank" rel="noreferrer">
              Submit Support Request <ExternalIcon icon="external-link-alt" />
            </ExternalAnchor>
            <a href="mailto:help@ruddr.io">Email</a>
          </Actions>
        </Block>
        {actions.canSubscribe &&
          (() => {
            if (trialInfo) {
              const isExpiringSoon = trialInfo.daysLeft <= 5;
              const TrialBlock = isExpiringSoon ? WarningHighlightBlock : HighlightBlock;
              return (
                <TrialBlock>
                  <Content>
                    <Title>Trial Info</Title>
                    <Description>
                      {isExpiringSoon ? (
                        <>
                          Your trial ends in{' '}
                          <strong>
                            {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? 'day' : 'days'}
                          </strong>
                          . Click &ldquo;Subscribe&rdquo; below to set up your billing information and prevent an
                          interruption of service, or request a trial extension if you need more time to evaluate the
                          platform. You will not be charged until your trial ends.
                        </>
                      ) : (
                        <>
                          You are currently in a trial. Click &ldquo;Subscribe&rdquo; below to set up your billing
                          information and prevent an interruption of service at the end of the trial. You will not be
                          charged until your trial ends.
                        </>
                      )}
                    </Description>
                  </Content>
                  <Actions>
                    <Button isAnchor={true} isLoading={isSubscribing} onClick={handleSubscribe}>
                      Subscribe
                    </Button>
                    {isExpiringSoon && (
                      <Button isAnchor={true} isLoading={trialExtensionLoading} onClick={requestTrialExtension}>
                        Request Trial Extension
                      </Button>
                    )}
                  </Actions>
                </TrialBlock>
              );
            }
            return (
              <DangerHighlightBlock>
                <Content>
                  <Title>Trial Info</Title>
                  <Description>
                    Your trial has ended. Click &ldquo;Subscribe&rdquo; below to set up your billing information and
                    continue using Ruddr, or request a trial extension if you need more time to evaluate the platform.
                  </Description>
                </Content>
                <Actions>
                  <Button isAnchor={true} isLoading={isSubscribing} onClick={handleSubscribe}>
                    Subscribe
                  </Button>
                  <Button isAnchor={true} isLoading={trialExtensionLoading} onClick={requestTrialExtension}>
                    Request Trial Extension
                  </Button>
                </Actions>
              </DangerHighlightBlock>
            );
          })()}
      </Blocks>
      {cancelSubscription && (
        <CancelSubscription onClose={() => setCancelSubscription(false)} onSaved={(data) => setBillingData(data)} />
      )}
      {undoCancelSubscription && (
        <UndoCancelSubscription
          onClose={() => setUndoCancelSubscription(false)}
          onSaved={(data) => setBillingData(data)}
        />
      )}
      {editContact && (
        <EditContact contact={contact} onClose={() => setEditContact(false)} onSaved={(data) => setBillingData(data)} />
      )}
      {editPaymentMethod && (
        <EditPaymentMethod onClose={() => setEditPaymentMethod(false)} onSaved={(data) => setBillingData(data)} />
      )}
      {removePaymentMethod && (
        <RemovePaymentMethod onClose={() => setRemovePaymentMethod(false)} onSaved={(data) => setBillingData(data)} />
      )}
      {editPlan && (
        <EditPlan
          billingData={billingData}
          onClose={() => setEditPlan(false)}
          onSaved={(data) => setBillingData(data)}
        />
      )}
      {viewBillingHistory && <ViewBillingHistory onClose={() => setViewBillingHistory(false)} />}
      {viewInvoices && <ViewInvoices onClose={() => setViewInvoices(false)} />}
    </>
  );
}
